import { Location } from 'history';

import { LocationState } from 'scenes/common/types';
import {
  MotorStepType,
  MotorFlowType,
  MotorGroupStepType,
  MotorFunnelState,
} from 'scenes/motor/types';

export const pathToStepType = (path: string) =>
  path
    ? (path.substring(path.lastIndexOf('/') + 1) as MotorStepType)
    : MotorStepType.Name;

export const stepOrder = [
  MotorStepType.PolicyStartDate,
  MotorStepType.OvernightParking,
  MotorStepType.RegoSearch,
  MotorStepType.VehicleDetails,
  MotorStepType.VehicleManualSelect,
  MotorStepType.GenderAndDateOfBirth,
  MotorStepType.YearsDriving,
  MotorStepType.ClaimsMade,
  MotorStepType.PayableClaims,
  MotorStepType.RecentInsurer,
  MotorStepType.UsedForBusiness,
  MotorStepType.BusinessType,
  MotorStepType.Finance,
  MotorStepType.VehicleFinancier,
  MotorStepType.AddDriver,
  MotorStepType.CoverForDriversUnder25,
  MotorStepType.FlybuysNumber,
  MotorStepType.PromoCode,
  MotorStepType.QuotePrice,
  MotorStepType.Excess,
  MotorStepType.MarketAgreedValue,
  MotorStepType.OptionalExtras,
  MotorStepType.addDriverName,
  MotorStepType.Name,
  MotorStepType.PersonalOrBusinessPolicy,
  MotorStepType.BusinessNameAndAbn,
  MotorStepType.BusinessRegisteredForGst,
  MotorStepType.AddPolicyHolder,
  MotorStepType.PersonalPolicyContact,
  MotorStepType.BusinessContact,
  MotorStepType.ContactPhoneNumber,
  MotorStepType.ContactEmailAddress,
  MotorStepType.PostalAddress,
  MotorStepType.DifferentPostalAddress,
  MotorStepType.DifferentPostalAddressManual,
  MotorStepType.LockedBagAddress,
  MotorStepType.MissingRegoStep,
  MotorStepType.PreExistingDamage,
  MotorStepType.Modifications,
  MotorStepType.ModificationSelection,
  MotorStepType.SpecialFeatures,
  MotorStepType.SpecialDetails,
  MotorStepType.InsuredSincePossessed,
  MotorStepType.TermsAndConditions,
  MotorStepType.Summary,
  MotorStepType.PaymentFrequency,
  MotorStepType.PaymentDate,
  MotorStepType.PaymentMethod,
  MotorStepType.DirectDebitPayment,
  MotorStepType.CreditCardPayment,
  MotorStepType.PaymentConfirmation,
  MotorStepType.ReviewPolicyStartDate,
  MotorStepType.ReviewDriverDetails,
];

const progressFromStep = (step: MotorStepType) => {
  const index = stepOrder.findIndex((p) => p === step);
  return index ? (index / stepOrder.length) * 100 : 0;
};

const progressFromPosition = (
  step: MotorStepType,
  { flow }: { flow?: MotorFlowType } = {}
) => {
  if (flow === MotorFlowType.AddDriver)
    return progressFromStep(MotorStepType.AddDriver);
  return progressFromStep(step);
};

const stepsWithoutProgress = [MotorStepType.PaymentConfirmation];

export const stepHasProgress = (
  location: Location<LocationState<MotorFlowType>>
) => !stepsWithoutProgress.includes(pathToStepType(location.pathname));

export const progress = (location: Location<LocationState<MotorFlowType>>) =>
  progressFromPosition(pathToStepType(location.pathname), location.state);

// Group Steps
const groupStepOrder = {
  [MotorGroupStepType.CarDetails]: [
    MotorStepType.PolicyStartDate,
    MotorStepType.OvernightParking,
    MotorStepType.RegoSearch,
    MotorStepType.VehicleDetails,
    MotorStepType.VehicleManualSelect,
  ],
  [MotorGroupStepType.DriverDetails]: [
    MotorStepType.GenderAndDateOfBirth,
    MotorStepType.YearsDriving,
    MotorStepType.ClaimsMade,
    MotorStepType.PayableClaims,
  ],
  [MotorGroupStepType.CarUsage]: [
    MotorStepType.RecentInsurer,
    MotorStepType.UsedForBusiness,
    MotorStepType.BusinessType,
    MotorStepType.Finance,
    MotorStepType.VehicleFinancier,
  ],
  [MotorGroupStepType.OtherDetails]: [
    MotorStepType.AddDriver,
    MotorStepType.GenderAndDateOfBirth,
    MotorStepType.YearsDriving,
    MotorStepType.ClaimsMade,
    MotorStepType.PayableClaims,
    MotorStepType.CoverForDriversUnder25,
    MotorStepType.FlybuysNumber,
  ],
  [MotorGroupStepType.Quote]: [MotorStepType.QuotePrice],
  [MotorGroupStepType.Customise]: [
    MotorStepType.Excess,
    MotorStepType.MarketAgreedValue,
    MotorStepType.OptionalExtras,
  ],
  [MotorGroupStepType.PolicyDetails]: [
    MotorStepType.addDriverName,
    MotorStepType.Name,
    MotorStepType.PersonalOrBusinessPolicy,
    MotorStepType.BusinessNameAndAbn,
    MotorStepType.BusinessRegisteredForGst,
    MotorStepType.AddPolicyHolder,
    MotorStepType.PersonalPolicyContact,
    MotorStepType.BusinessContact,
    MotorStepType.ContactPhoneNumber,
    MotorStepType.ContactEmailAddress,
    MotorStepType.PostalAddress,
    MotorStepType.DifferentPostalAddress,
    MotorStepType.DifferentPostalAddressManual,
    MotorStepType.LockedBagAddress,
    MotorStepType.MissingRegoStep,
    MotorStepType.PreExistingDamage,
    MotorStepType.Modifications,
    MotorStepType.ModificationSelection,
    MotorStepType.SpecialFeatures,
    MotorStepType.SpecialDetails,
    MotorStepType.InsuredSincePossessed,
    MotorStepType.TermsAndConditions,
    MotorStepType.Summary,
  ],
  [MotorGroupStepType.Payment]: [
    MotorStepType.PaymentFrequency,
    MotorStepType.PaymentDate,
    MotorStepType.PaymentMethod,
    MotorStepType.DirectDebitPayment,
    MotorStepType.CreditCardPayment,
    MotorStepType.PaymentConfirmation,
    MotorStepType.ReviewPolicyStartDate,
    MotorStepType.ReviewDriverDetails,
  ],
};

export const getCurrentGroupStep = (
  step: MotorStepType,
  flow: MotorFlowType
) => {
  if (flow === MotorFlowType.AddDriver) return MotorGroupStepType.OtherDetails;

  const currentGroupStep = Object.values(MotorGroupStepType).find((group) =>
    groupStepOrder[group].includes(step)
  );
  if (currentGroupStep) return currentGroupStep;
  return MotorGroupStepType.CarDetails;
};

const calculateProgress = (
  currentGroupStep: MotorGroupStepType,
  flow: MotorFlowType,
  step: MotorStepType
) => {
  if (
    (currentGroupStep === MotorGroupStepType.OtherDetails &&
      flow === MotorFlowType.AddDriver) ||
    (currentGroupStep === MotorGroupStepType.PolicyDetails &&
      step === MotorStepType.Name)
  )
    return (0 / groupStepOrder[currentGroupStep].length) * 100;

  const index = groupStepOrder[currentGroupStep].findIndex((p) => p === step);
  return (index / groupStepOrder[currentGroupStep].length) * 100;
};

const progressFromGroupStep = (step: MotorStepType, flow: MotorFlowType) => {
  const currentGroupStep = getCurrentGroupStep(step, flow);
  const stepNumber =
    Object.values(MotorGroupStepType).findIndex(
      (group) => group === currentGroupStep
    ) + 1;

  return {
    progress: calculateProgress(currentGroupStep, flow, step),
    currentProgressBarFlow: currentGroupStep,
    stepNumber,
  };
};

export const progressGroup = (funnelState: MotorFunnelState) =>
  progressFromGroupStep(funnelState.step, funnelState.flow);
